import {
  Flex,
  FlexProps,
  Link,
  LinkProps,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import omit from "lodash/omit";
import { Link as RouterLink } from "react-router";

import { highlightStringMatch } from "@/common/components/Autocomplete/AutocompleteSelect";
import { Avatar, AvatarSizeProp } from "@/common/components/Avatar";
import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { CompanyName } from "@reflag/shared/companyAPI";
import { CompanyUrl } from "@reflag/shared/urls";
import { OptionalKeys } from "@reflag/shared/utils/types";

// Discriminated union for link handling, similar to FeatureDisplay/SegmentDisplay
export type CompanyDisplayProps = {
  company: OptionalKeys<CompanyName, "name" | "avatarUrl">;
  search?: string;
  showId?: boolean;
  avatarSize?: AvatarSizeProp;
} & (
  | ({ link: true } & Omit<LinkProps, "size">)
  | ({ link?: false | undefined } & FlexProps)
);

export default function CompanyDisplay({
  company,
  search,
  showId = false,
  avatarSize,
  ...rest
}: CompanyDisplayProps) {
  const { env } = useEnvironment();
  const displayText = company.name || company.id;
  const avatarUrl = company.avatarUrl;
  const dimmedColor = useColorModeValue("gray.400", "gray.600");
  const commonFlexProps = {
    display: "flex",
    alignItems: "center",
    minW: 0,
    gap: 2,
    isTruncated: true,
  };

  const content = (
    <>
      <Avatar
        borderRadius={4}
        name={!company.isUnknown ? displayText : undefined}
        size={avatarSize}
        src={avatarUrl ?? undefined}
        type="company"
      />
      <Text color={company.isUnknown ? dimmedColor : undefined} isTruncated>
        {search ? highlightStringMatch(displayText, search) : displayText}
      </Text>
      {company.name && showId ? (
        <Text color={dimmedColor} isTruncated>
          {search ? highlightStringMatch(company.id, search) : company.id}
        </Text>
      ) : null}
    </>
  );

  if (rest.link) {
    return (
      <Link
        as={RouterLink}
        textDecoration="none"
        to={CompanyUrl(env, company.id)}
        {...commonFlexProps}
        {...omit(rest, "link")}
      >
        {content}
      </Link>
    );
  }

  return (
    <Flex {...commonFlexProps} {...omit(rest, "link")}>
      {content}
    </Flex>
  );
}
